// Animation route (Jun 11) — timeline model + style catalogs.
//
// ONE unified spec: an animation is an image sequence driven by a
// MOTION style, with an optional animated TEXT overlay driven by its
// own TEXT style. There is no "animation type" — text is a layer on
// top of the motion, not a separate mode (user-locked Jun 11).
//
// The SAME spec drives both the live preview (rAF loop drawing into a
// visible canvas) and the MP4 export (deterministic frame stepping into
// an offscreen canvas → WebCodecs) — so what you see looping in the
// preview is byte-for-byte what gets encoded.
//
// All rendering happens client-side on canvas. Zero backend cost per
// animation — the 1-credit export charge is pure margin.

export type AnimAspect = '9:16' | '4:5' | '1:1';

export const ANIM_DIMENSIONS: Record<AnimAspect, { w: number; h: number }> = {
  '9:16': { w: 1080, h: 1920 },
  // 4:5 — Instagram feed portrait standard. 1080×1350.
  '4:5':  { w: 1080, h: 1350 },
  '1:1':  { w: 1080, h: 1080 },
};

export const ANIM_FPS = 30;

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

export type MotionStyle =
  | 'crossfade'
  | 'kenburns'
  | 'slide'
  | 'zoom-punch'
  | 'wipe'
  | 'flash-cut'
  | 'mosaic-scroll'
  | 'card-swipe'
  | 'marquee-rows'
  | 'stop-motion-grid'
  | 'angled-cut'
  | 'collage-stack';

/** Per-motion-style config (template-driven). When unset the renderer falls
 *  back to baked-in defaults — preserves the existing 6 styles which have
 *  hardcoded constants. New tunable styles (mosaic-scroll, card-swipe) expose
 *  their knobs via this discriminated union so templates can vary feel
 *  without code changes. */
export type MotionConfig =
  | MosaicScrollConfig
  | CardSwipeConfig
  | MarqueeRowsConfig
  | StopMotionGridConfig
  | AngledCutConfig
  | CollageStackConfig;

export interface MosaicScrollConfig {
  kind: 'mosaic-scroll';
  /** Number of vertical columns (default 3). */
  columns?: number;
  /** Ease-in-out duration per scroll step in ms (default 1200). */
  scrollMs?: number;
  /** Pause duration between scroll steps in ms (default 800). */
  holdMs?: number;
  /** Phase offset between adjacent columns in ms (default 200). Creates the
   *  asymmetric "alive" feel — without it, all 3 columns scroll in lockstep
   *  and look like a single video wall. */
  columnStaggerMs?: number;
}

export interface CardSwipeConfig {
  kind: 'card-swipe';
  /** Ease-in-out duration per transition in ms (default 800). */
  transitionMs?: number;
  /** Hold duration at each card (rest between transitions) in ms (default 1500). */
  holdMs?: number;
  /** Center card width as a fraction of canvas width (default 0.55). The
   *  side cards peek through at (canvasW - CARD_WIDTH) / 2 minus GAP each. */
  cardWidthFrac?: number;
  /** Card height as a fraction of canvas height (default 0.75). Determines
   *  how much gray bg shows above/below the card. */
  cardHeightFrac?: number;
}

export interface MarqueeRowsConfig {
  kind: 'marquee-rows';
  /** Ease-in-out duration per scroll step in ms (default 1500). */
  scrollMs?: number;
  /** Pause duration between scroll steps in ms (default 700). */
  holdMs?: number;
  /** Phase offset between adjacent rows in ms (default 250). Creates the
   *  "alive" feel — without it, all 3 rows scroll in lockstep and look like
   *  one big image. */
  rowPhaseMs?: number;
  /** Tile width as a fraction of canvas width (default 1.0 = one full image
   *  per row at a time, parts of prev/next peeking at the edges as it
   *  slides). Lower values fit more tiles per row. */
  tileWidthFrac?: number;
  /** When true, each row renders ONE third of the image (row 0 = top strip,
   *  row 1 = middle, row 2 = bottom) instead of the full image cover-fit.
   *  During the hold phase the strips align horizontally → the puzzle
   *  "assembles" into the full image (with gap bands between rows). For the
   *  reveal to land, holdMs must comfortably exceed 2 × rowPhaseMs so all 3
   *  rows overlap in hold. Default false. */
  splitImage?: boolean;
}

export interface StopMotionGridConfig {
  kind: 'stop-motion-grid';
  /** Grid columns + rows (default 3×2 = 6 cells). */
  cols?: number;
  rows?: number;
  /** Per-tile tick period range in ms (each tile picks a deterministic period
   *  in this range, so the grid feels alive — some tiles change fast, others
   *  slow). Defaults 600 / 1800. */
  tickMinMs?: number;
  tickMaxMs?: number;
  /** Probability each tile shows an image at a given tick (vs. blank
   *  background). 0..1. Default 0.8 — mostly populated mood board with the
   *  occasional empty cell to break the rhythm. */
  showProbability?: number;
  /** Tile aspect ratio (height / width). Default 1 (square). */
  tileAspectRatio?: number;
}

export interface AngledCutConfig {
  kind: 'angled-cut';
  /** Cut axis. 'horizontal' = near-horizontal line, regions slide in from
   *  top/bottom; 'vertical' = near-vertical line, regions slide in from
   *  left/right. Default 'horizontal' (Jun 13). */
  orientation?: 'vertical' | 'horizontal';
  /** Tilt of the split line in degrees AWAY FROM the orientation's base
   *  axis (default 12°). With orientation='horizontal' and tiltDeg=12, the
   *  line is mostly horizontal, tilted 12° (left edge higher than right).
   *  With orientation='vertical' and tiltDeg=12, the line is mostly vertical,
   *  tilted 12° from vertical (top to the left of bottom). */
  tiltDeg?: number;
  /** Ease duration per slide-in in ms (default 1100). */
  scrollMs?: number;
  /** Hold duration once both regions are assembled (default 1500). */
  holdMs?: number;
  /** Phase offset between the two regions in ms (default 350). The first
   *  region (top for horizontal / left for vertical) arrives first; the
   *  second region lands `regionPhaseMs` later — gives the sliced cascade
   *  feel rather than a synced reveal. */
  regionPhaseMs?: number;
}

export interface CollageStackConfig {
  kind: 'collage-stack';
  /** Time between successive image-additions in ms (default 700). New image
   *  appears on top of the stack at each tick. No easing — discrete adds. */
  tickMs?: number;
  /** Hold duration after all images are stacked, before the cycle restarts
   *  with a blank canvas (default 1800). */
  holdMs?: number;
  /** Max rotation per card in degrees (default 8 = each card tilts up to ±8°).
   *  Higher = more chaotic scrapbook; lower = clean stack. */
  maxRotationDeg?: number;
  /** Card width as a fraction of canvas width, randomized per card in
   *  [base − jitter, base + jitter] (defaults 0.65 / 0.1 → 0.55..0.75). */
  cardWidthFrac?: number;
  cardWidthJitter?: number;
}

export type TextStyle = 'word-pop' | 'slide-in' | 'typewriter';

// ---------------------------------------------------------------------------
// Spec
// ---------------------------------------------------------------------------

export interface AnimationTextOverlay {
  style: TextStyle;
  headline: string;
  subline?: string;
  textColor: string;
  /** Highlight color — typewriter caret, word-pop subline, etc. */
  accentColor: string;
}

export interface AnimationSpec {
  /** Preloaded, decode-complete images (cover-fit at draw time). 1–6.
   *  A single image still animates (Ken Burns drift / static hold) —
   *  useful as a text-overlay backdrop. */
  images: HTMLImageElement[];
  motionStyle: MotionStyle;
  /** Per-motion-style tunables. Set by animation templates; free-build leaves
   *  it undefined → renderer uses its baked-in defaults per style. */
  motionConfig?: MotionConfig;
  /** Canvas background color (hex, e.g. '#EDEDED'). Only visible for motion
   *  styles that don't cover the canvas edge-to-edge: mosaic-scroll (between
   *  gapped tiles) and card-swipe (around the floating card). Defaults to
   *  '#EDEDED' light gray when undefined. */
  backgroundColor?: string;
  /** Per-image hold INCLUDING its outgoing transition. */
  perImageMs: number;
  /** Cross-image transition window. Must be < perImageMs. */
  transitionMs: number;
  /** Optional TITLE layer — spans the WHOLE video, centered. Entrance
   *  timing is paced against the total duration (entrances complete by
   *  ~60%, then hold). */
  text?: AnimationTextOverlay;
  /** Tier-1 timing model (Jun 11): per-slide CAPTIONS, aligned by index
   *  GLOBAL CYCLING captions (Jun 15 — was per-image / slide-indexed).
   *  List of non-empty caption strings, INDEPENDENT of images. The
   *  renderer divides total animation duration evenly across this list;
   *  each caption gets totalMs/N time on screen, in order, then cycles
   *  with the rest of the loop. Empty / whitespace-only entries should
   *  be filtered out by the producer before passing in. */
  captions?: string[];
  /** Text style applied to all slide captions. Defaults to 'slide-in'. */
  captionStyle?: TextStyle;
  /** Caption typography — family must be one of the self-hosted
   *  @fontsource families (see ANIM_FONTS) at a loaded weight, or
   *  canvas silently falls back to a system font and the export drifts
   *  from the preview. Defaults: Manrope 800. */
  captionFont?: string;
  captionWeight?: number;
  /** Caption size multiplier (0.6–2.0, default 1) on the base
   *  width-relative size. */
  captionScale?: number;
  /** Caption placement: horizontal alignment + vertical center of the
   *  text block as a 0..1 fraction of canvas height. Defaults:
   *  center / 0.8 (lower third). The legibility scrim follows the
   *  block wherever it sits. */
  captionAlign?: 'left' | 'center' | 'right';
  captionY?: number;
  /** Caption text color (any CSS color string). Defaults to white. */
  captionTextColor?: string;
  /** Caption background fill — when set, renders as per-line rounded
   *  "highlighter" pills hugging each text line. Null/undefined = no
   *  background (text on the soft gradient scrim only). */
  captionBgColor?: string | null;
  /** Legibility scrim style drawn BEHIND the caption block (separate
   *  from the per-line pill fill). Jul 24 — was a single fixed centered
   *  band; now selectable so the darkening can come from below / above /
   *  a centered soft band, or be turned off entirely. Defaults to 'band'
   *  (the original behavior). */
  captionScrim?: CaptionScrim;
}

/** Backdrop-scrim variants behind captions.
 *   - 'none'   → no scrim (text on bare footage)
 *   - 'band'   → soft dark band centered on the caption block (original)
 *   - 'bottom' → gradient rising from the bottom edge (classic lower-third)
 *   - 'top'    → gradient falling from the top edge */
export type CaptionScrim = 'none' | 'band' | 'bottom' | 'top';

// ---------------------------------------------------------------------------
// Caption fonts — curated subset of the self-hosted @fontsource families
// (frontend/src/fonts.ts), each at a weight that is ACTUALLY loaded.
// Bebas Neue + Anton are single-weight display fonts (400 only).
// ---------------------------------------------------------------------------

export interface AnimFont {
  family: string;
  weight: number;
  label: string;
}

export const ANIM_FONTS: AnimFont[] = [
  { family: 'Manrope', weight: 800, label: 'Manrope' },
  { family: 'Bebas Neue', weight: 400, label: 'Bebas' },
  { family: 'Anton', weight: 400, label: 'Anton' },
  { family: 'Playfair Display', weight: 700, label: 'Playfair' },
  { family: 'Fraunces', weight: 700, label: 'Fraunces' },
  { family: 'Space Grotesk', weight: 700, label: 'Grotesk' },
];

/** Total timeline duration for a spec.
 *
 *  Baseline: N images × perImageMs.
 *
 *  Multi-cycle motions (marquee-rows, mosaic-scroll, card-swipe, angled-cut)
 *  have an internal cycle (scrollMs + holdMs) that's independent of the
 *  user's perImage slider. When N × perImageMs is shorter than the motion's
 *  natural N × cycleMs, the animation cuts off mid-motion → preview loop
 *  stutters. Fix (Jun 15 v3): for those motions, round the duration UP to
 *  the nearest multiple of (N × cycleMs) so the wrap aligns and the loop
 *  seam disappears.
 *
 *  Stop-motion-grid + crossfade/kenburns/etc. have no fixed cycle so they
 *  use the baseline N × perImageMs directly.
 */
export function specDurationMs(spec: AnimationSpec): number {
  const N = spec.images.length;
  const baseline = N * spec.perImageMs;
  if (N === 0) return baseline;

  // Collage-stack has its OWN cycle: total × tickMs + holdMs.
  // Loop seam aligns when duration % cycleMs === 0 (only one image stack
  // per cycle, no N-multiplier — each image lands once per cycle, then
  // the canvas resets for the next stack).
  if (spec.motionConfig?.kind === 'collage-stack') {
    const tickMs = Math.max(150, spec.motionConfig.tickMs ?? 700);
    const holdMs = Math.max(0, spec.motionConfig.holdMs ?? 1800);
    const cycleMs = N * tickMs + holdMs;
    const padded = Math.max(baseline, cycleMs);
    return Math.ceil(padded / cycleMs) * cycleMs;
  }

  // Mosaic-scroll has its own loop math (Jun 15 v3 — was approximated
  // by N × cycleMs which left a visible "small jerk" when wrap fired
  // mid-scroll). The column scrolls SCROLL_PER_CYCLE = h × 0.45 pixels
  // per cycle, wrapping on `baseContent` (per-column image-height sum).
  // For a seamless loop, total scroll over duration must be an INTEGER
  // multiple of baseContent:
  //   total_scroll = (durationMs / cycleMs) × (h × 0.45) = K × baseContent
  //   durationMs   = K × cycleMs × baseContent / (h × 0.45)
  //
  // baseContent depends on image aspects + canonical canvas geometry.
  // Use a 4:5 canonical canvas (1080×1350) — the canvas-aspect dependence
  // is small (column width scales with canvas width, image scaling scales
  // proportionally) so this alignment is approximate across aspects but
  // tight enough to remove the visible jerk in the 4:5 default case.
  if (spec.motionConfig?.kind === 'mosaic-scroll') {
    const cfg = spec.motionConfig;
    const scrollMs = Math.max(200, cfg.scrollMs ?? 1200);
    const holdMs = Math.max(0, cfg.holdMs ?? 800);
    const cycleMs = scrollMs + holdMs;

    const CANON_W = 1080;
    const CANON_H = 1350;
    const COL_W = CANON_W * 0.6;
    // GAP is proportional to canvas height in the renderer (Jun 15 v4)
    // so this canonical formula stays in sync: at any rendered size,
    // baseContent / SCROLL_PER_CYCLE is the same ratio → loop alignment
    // computed here works at preview AND export sizes.
    const GAP = CANON_H * 0.06;
    const COLUMNS = 3;
    const SCROLL_PER_CYCLE = CANON_H * 0.45;

    // Mirror the renderer's distribution (Jun 15 v4 — center-priority).
    // All cols now have the SAME image set (col 1 in natural order, cols
    // 0/2 rotated). Same images → same heights → same baseContent across
    // all columns, so we can compute from any column's image set.
    let passContent = 0;
    for (const img of spec.images) {
      const iw = img.naturalWidth || 1;
      const ih = img.naturalHeight || 1;
      const ratio = ih / iw;
      const tileH = COL_W * (Number.isFinite(ratio) && ratio > 0 ? ratio : 1);
      passContent += tileH + GAP;
    }
    if (passContent <= 0) return baseline;
    const repeatCount = Math.min(30, Math.max(1, Math.ceil(CANON_H / passContent)));
    const maxBaseContent = passContent * repeatCount;

    // durationMs per integer K of full baseContent traversals.
    const durationPerK = (maxBaseContent * cycleMs) / SCROLL_PER_CYCLE;
    // Pick the smallest K such that durationMs ≥ baseline (at least one
    // full cycle worth of preview / per-image hold time).
    const K = Math.max(1, Math.ceil(baseline / durationPerK));
    return Math.round(K * durationPerK);
  }

  // Helper — derive the motion's internal cycle from motionConfig for
  // motions where the full loop period is N × cycleMs (marquee, card-
  // swipe, angled-cut). Returns null when the motion has no fixed cycle
  // (caller falls back to baseline).
  const cycleMs = (() => {
    const cfg = spec.motionConfig;
    if (!cfg) return null;
    if (cfg.kind === 'marquee-rows') {
      return Math.max(200, cfg.scrollMs ?? 1500) + Math.max(0, cfg.holdMs ?? 700);
    }
    if (cfg.kind === 'card-swipe') {
      return Math.max(200, cfg.transitionMs ?? 800) + Math.max(0, cfg.holdMs ?? 1700);
    }
    if (cfg.kind === 'angled-cut') {
      return Math.max(200, cfg.scrollMs ?? 1100) + Math.max(0, cfg.holdMs ?? 1700);
    }
    return null;
  })();

  if (cycleMs == null) return baseline;

  // Wrap math in the renderers uses contentW = N × STRIDE for the row /
  // column scroll, so the full loop period is N × cycleMs. Round baseline
  // UP to the nearest multiple → the modulo at t=duration aligns with
  // t=0 → seamless loop.
  const pairCycle = N * cycleMs;
  const padded = Math.max(baseline, pairCycle);
  return Math.ceil(padded / pairCycle) * pairCycle;
}

// ---------------------------------------------------------------------------
// Style catalogs (drive the picker UI)
// ---------------------------------------------------------------------------

export interface StyleMeta<T extends string = string> {
  id: T;
  name: string;
  description: string;
  icon: string;
}

export const MOTION_STYLES: StyleMeta<MotionStyle>[] = [
  {
    id: 'crossfade',
    name: 'Crossfade',
    description: 'Soft dissolve between images — calm, premium.',
    icon: 'ri-contrast-drop-2-line',
  },
  {
    id: 'kenburns',
    name: 'Ken Burns',
    description: 'Slow zoom + drift on each image — makes stills feel alive.',
    icon: 'ri-focus-3-line',
  },
  {
    id: 'slide',
    name: 'Slide',
    description: 'Images push in from the right — snappy, energetic.',
    icon: 'ri-arrow-left-right-line',
  },
  {
    id: 'zoom-punch',
    name: 'Zoom Punch',
    description: 'Hard cuts with a punch-in on every image — beat-drop energy.',
    icon: 'ri-zoom-in-line',
  },
  {
    id: 'wipe',
    name: 'Wipe',
    description: 'Next image sweeps across with a clean edge.',
    icon: 'ri-arrow-right-wide-line',
  },
  {
    id: 'flash-cut',
    name: 'Flash Cut',
    description: 'Hard cuts through a white flash — fast, fashion-editorial.',
    icon: 'ri-flashlight-line',
  },
  {
    id: 'mosaic-scroll',
    name: 'Mosaic Scroll',
    description: 'Multi-column grid that scrolls up with ease + brief pauses.',
    icon: 'ri-layout-grid-line',
  },
  {
    id: 'card-swipe',
    name: 'Card Swipe',
    description: 'Single hero card eases right-to-left with previous/next peeks.',
    icon: 'ri-slideshow-line',
  },
  {
    id: 'marquee-rows',
    name: 'Marquee Rows',
    description: 'Three horizontal rows sliding the same image right-to-left.',
    icon: 'ri-menu-line',
  },
  {
    id: 'stop-motion-grid',
    name: 'Mood Board',
    description: 'Grid of tiles that flip in and out with stop-motion feel.',
    icon: 'ri-grid-line',
  },
  {
    id: 'angled-cut',
    name: 'Angled Cut',
    description: 'Image split by an angled near-vertical line; halves snap to assemble.',
    icon: 'ri-scissors-cut-line',
  },
  {
    id: 'collage-stack',
    name: 'Collage Stack',
    description: 'Images stop-motion onto a stack — slight rotation, paper-photo feel.',
    icon: 'ri-stack-line',
  },
];

export const TEXT_STYLES: StyleMeta<TextStyle>[] = [
  {
    id: 'word-pop',
    name: 'Word Pop',
    description: 'Words pop in one by one with a springy scale.',
    icon: 'ri-text-spacing',
  },
  {
    id: 'slide-in',
    name: 'Slide In',
    description: 'Lines glide up into place, staggered.',
    icon: 'ri-arrow-up-line',
  },
  {
    id: 'typewriter',
    name: 'Typewriter',
    description: 'Characters type out with a blinking caret.',
    icon: 'ri-keyboard-line',
  },
];

/** Per-URL in-memory cache so re-selecting an already-loaded image returns
 *  instantly instead of round-tripping the HTTP cache (Jun 15 v3). Keyed
 *  by src; value is the decode Promise so simultaneous callers share one
 *  load. Failed loads remove themselves from the cache so retries work.
 *
 *  Scoped at module level — survives across remounts and route changes
 *  within the session. Page reload clears it (acceptable; HTTP cache
 *  takes over). Memory cost is bounded by the user's gallery size
 *  (~20-50 images typical) which is fine.
 */
const _animImageCache: Map<string, Promise<HTMLImageElement>> = new Map();

/** Load an image URL into a decode-complete HTMLImageElement.
 *  crossOrigin=anonymous so GCS-hosted creatives don't taint the canvas
 *  (the gallery buckets have a permissive CORS rule). Data URLs pass
 *  through unaffected. Cached per-URL (Jun 15 v3) — re-loading the same
 *  URL returns the same Image instance, eliminating fetch latency on
 *  image-set changes (the main cause of "preview jitter on add/remove"
 *  in the Animation Studio).
 */
export function loadAnimImage(src: string): Promise<HTMLImageElement> {
  const cached = _animImageCache.get(src);
  if (cached) return cached;
  const promise = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => {
      _animImageCache.delete(src);   // let retry succeed after transient errors
      reject(new Error('Failed to load image'));
    };
    img.src = src;
  });
  _animImageCache.set(src, promise);
  return promise;
}
